/**
 * WorksheetDays.jsx - the workbook's "2. WorkSheet" for one engineer at a
 * time: a row per date (Date, Day, Expense, Task, Hours, then the day's sell
 * and - 'cost-rates' only - cost, as the server worked them out), and a Fill
 * bar that types a whole range at once (Mon-Fri / Saturday / Sunday hours,
 * the task and the expense flag), the way people filled the sheet by
 * dragging. Holidays and weekends are tinted; a holiday is never filled
 * unless its own hours are typed.
 */
import { useMemo, useState } from 'react';
import { Btn, Field, MUTED, NumInput, Section, SelectInput, TextInput, fmtMoney, fmtNum, inputStyle, th } from '../quotes/quoteUi';
import {
  clearRows,
  dayKind,
  dayName,
  fillRows,
  flagsOf,
  holidaySet,
  ledgerDates,
  setRow,
  tasksOf,
  updateEngineer,
} from '../quotes/worksheetDraft';

const TINT = { holiday: '#FFEBEE', sunday: '#FFF3E0', saturday: '#FFFDE7', weekday: undefined };
const cellIn = { ...inputStyle, padding: '3px 5px', fontSize: 11.5 };

export default function WorksheetDays({ ws, setWs, settings, calc, seesCosts, readOnly, active = 0, setActive }) {
  const ro = !!readOnly;
  const engineers = ws.engineers || [];
  const idx = Math.min(active, Math.max(0, engineers.length - 1));
  const eng = engineers[idx];
  const dates = useMemo(() => ledgerDates(ws.start, ws.days), [ws.start, ws.days]);
  const hol = useMemo(() => holidaySet(settings.holidays), [settings.holidays]);
  const tasks = tasksOf(settings);
  const flags = flagsOf(settings);
  const [fill, setFill] = useState({ from: '', to: '', task: '', flag: '', wd: '8', sat: '', sun: '', hol: '' });
  const cells = useMemo(() => {
    const m = new Map();
    for (const d of (calc && calc.days) || []) for (const c of d.cells) if (c.engineer === idx) m.set(d.date, c);
    return m;
  }, [calc, idx]);
  if (!eng) return null;
  const rows = eng.rows || {};
  const setRows = (fn) => setWs((w) => updateEngineer(w, idx, (e) => ({ ...e, rows: fn(e.rows || {}) })));
  const label = (e, i) => e.name || (e.level ? `${e.level} engineer` : `Engineer ${i + 1}`);
  const outside = Object.keys(rows).filter((d) => !dates.includes(d)).length;

  return (
    <Section
      title="2  WorkSheet"
      hint="Each engineer's days: the expense flag, the task and the hours. Weekdays bill 8 hours straight and the rest at 1.3; Saturday 1.3; Sunday and holidays 1.7; Travel always straight."
    >
      <div role="tablist" aria-label="Engineer" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        {engineers.map((e, i) => (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={i === idx}
            onClick={() => setActive(i)}
            style={{
              padding: '5px 12px',
              borderRadius: 16,
              fontSize: 11,
              fontWeight: 700,
              fontFamily: 'Montserrat',
              cursor: 'pointer',
              border: `1px solid ${i === idx ? '#1B5E20' : '#D6DCCB'}`,
              background: i === idx ? '#1B5E20' : '#fff',
              color: i === idx ? '#fff' : '#1F2328',
            }}
          >
            {label(e, i)}
            {calc && calc.engineers[i] ? ` · ${fmtNum(calc.engineers[i].hours)} h` : ''}
          </button>
        ))}
      </div>

      {!ro && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(9, minmax(0, 1fr))',
            gap: 8,
            alignItems: 'end',
            padding: 10,
            background: '#F6F8F3',
            borderRadius: 8,
            marginBottom: 10,
          }}
        >
          <Field label="From">
            <TextInput type="date" aria-label="Fill from" value={fill.from} onChange={(v) => setFill((f) => ({ ...f, from: v }))} />
          </Field>
          <Field label="To">
            <TextInput type="date" aria-label="Fill to" value={fill.to} onChange={(v) => setFill((f) => ({ ...f, to: v }))} />
          </Field>
          <Field label="Task">
            <SelectInput
              aria-label="Fill task"
              value={fill.task}
              blank="-"
              options={tasks}
              onChange={(v) => setFill((f) => ({ ...f, task: v }))}
            />
          </Field>
          <Field label="Expense">
            <SelectInput
              aria-label="Fill expense"
              value={fill.flag}
              blank="-"
              options={flags}
              onChange={(v) => setFill((f) => ({ ...f, flag: v }))}
            />
          </Field>
          <Field label="Mon-Fri hrs">
            <NumInput aria-label="Fill weekday hours" value={fill.wd} onChange={(v) => setFill((f) => ({ ...f, wd: v }))} />
          </Field>
          <Field label="Sat hrs">
            <NumInput aria-label="Fill Saturday hours" value={fill.sat} onChange={(v) => setFill((f) => ({ ...f, sat: v }))} />
          </Field>
          <Field label="Sun hrs">
            <NumInput aria-label="Fill Sunday hours" value={fill.sun} onChange={(v) => setFill((f) => ({ ...f, sun: v }))} />
          </Field>
          <Field label="Holiday hrs">
            <NumInput
              aria-label="Fill holiday hours"
              value={fill.hol}
              placeholder="0"
              onChange={(v) => setFill((f) => ({ ...f, hol: v }))}
            />
          </Field>
          <div style={{ display: 'flex', gap: 4 }}>
            <Btn small primary disabled={!fill.from || !fill.to} onClick={() => setRows((r) => fillRows(r, fill, hol))}>
              Fill
            </Btn>
            <Btn small disabled={!fill.from || !fill.to} onClick={() => setRows((r) => clearRows(r, fill.from, fill.to))}>
              Clear
            </Btn>
          </div>
        </div>
      )}
      {outside > 0 && (
        <div style={{ fontSize: 11, color: '#92400E', marginBottom: 6 }}>
          {outside} typed day(s) are outside the dates shown - they still count. Move the start date or add days to see them.
        </div>
      )}

      <div style={{ maxHeight: 520, overflowY: 'auto', border: '1px solid #EEF1E8', borderRadius: 8 }}>
        <table aria-label={`WorkSheet for ${label(eng, idx)}`} style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
            <tr>
              {['Date', 'Day', 'Expense', 'Task', 'Hours', 'NT / OT / DT', 'Sell', ...(seesCosts ? ['Cost'] : []), 'Expenses'].map((x) => (
                <th key={x} style={th}>
                  {x}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dates.map((d) => {
              const r = rows[d] || {};
              const c = cells.get(d);
              const kind = dayKind(d, hol);
              return (
                <tr key={d} style={{ background: TINT[kind] }}>
                  <td style={{ padding: '2px 6px', fontSize: 11.5, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{d}</td>
                  <td style={{ padding: '2px 6px', fontSize: 11, color: kind === 'holiday' ? '#B71C1C' : MUTED }}>
                    {kind === 'holiday' ? 'Holiday' : dayName(d).slice(0, 3)}
                  </td>
                  <td style={{ padding: 2, width: 130 }}>
                    <select
                      aria-label={`${d} expense`}
                      value={r.e || ''}
                      disabled={ro}
                      onChange={(ev) => setRows((x) => setRow(x, d, 'e', ev.target.value))}
                      style={cellIn}
                    >
                      <option value="" />
                      {flags.map((f) => (
                        <option key={f} value={f}>
                          {f}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: 2, width: 150 }}>
                    <select
                      aria-label={`${d} task`}
                      value={r.t || ''}
                      disabled={ro}
                      onChange={(ev) => setRows((x) => setRow(x, d, 't', ev.target.value))}
                      style={cellIn}
                    >
                      <option value="" />
                      {r.t && !tasks.includes(r.t) && <option value={r.t}>{r.t}</option>}
                      {tasks.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: 2, width: 64 }}>
                    <input
                      aria-label={`${d} hours`}
                      inputMode="decimal"
                      value={r.h ?? ''}
                      readOnly={ro}
                      onChange={(ev) => setRows((x) => setRow(x, d, 'h', ev.target.value.replace(/[^0-9.]/g, '')))}
                      style={{ ...cellIn, textAlign: 'right' }}
                    />
                  </td>
                  <td style={{ padding: '2px 6px', fontSize: 11, color: MUTED, whiteSpace: 'nowrap' }}>
                    {c && c.hours ? `${fmtNum(c.nt)} / ${fmtNum(c.ot)} / ${fmtNum(c.dt)}` : ''}
                  </td>
                  <td style={{ padding: '2px 6px', fontSize: 11.5, textAlign: 'right' }}>{c && c.sell ? fmtMoney(c.sell) : ''}</td>
                  {seesCosts && (
                    <td style={{ padding: '2px 6px', fontSize: 11.5, textAlign: 'right', color: '#6A1B9A' }}>
                      {c && c.cost ? fmtMoney(c.cost) : ''}
                    </td>
                  )}
                  <td style={{ padding: '2px 6px', fontSize: 11, textAlign: 'right', color: MUTED }}>
                    {c && (c.expB || c.expNB) ? fmtMoney((c.expB || 0) + (seesCosts ? c.expNB || 0 : 0)) : ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!dates.length && <div style={{ padding: 16, fontSize: 12, color: MUTED }}>Set the start date to see the days.</div>}
      </div>
    </Section>
  );
}
